/**
 * Get the Prebid ad unit properties for the specified
 * position (preroll or postroll)
 *
 * @returns Object
 */
const getVideoAdUnit = (position) => {
  console.log('HTLBID::bids.js::getVideoAdUnit');
  if (! ['preroll', 'postroll'].includes(position)) {
    return {};
  }
  const docWidth = window.innerWidth || document.documentElement.clientWidth;
  const deviceType = 768 <= docWidth ? 'desktop' : 'mobile';
  const deviceLabel = `${position}_${deviceType}`;
  const playerSize = 'desktop' === deviceType ? [[640, 480]] : [[400, 300]];

  const videoBids = window.htlbid.config.prebidGroups[deviceLabel] || {};

  // https://docs.prebid.org/dev-docs/adunit-reference.html
  const videoAdUnit = {
    code: position,
    mediaTypes: {
      video: {
        context: 'outstream',
        playerSize,
        mimes: ['video/mp4'],
        protocols: [3, 7],
        playbackmethod: [2, 3, 6],
        api: [1, 2, 7],
        placement: 5,
        plcmt: 2,
        minduration: 5,
        maxduration: 30,
        linearity: 1,
        skip: 1,
        skipafter: 15,
      },
    },
    bids: videoBids,
  };

  return videoAdUnit;
};

let videoAdded = false;

/**
 * Initialize the window.pbjs global with video ad unit
 * settings and configure Prebid to use Prebid Cache
 *
 * @returns Object
 */
const initializeUnits = () => {
  if (! videoAdded) {
    // only add the video and configure prebid once
    videoAdded = true;
    const prerollUnit = getVideoAdUnit('preroll');
    const postrollUnit = getVideoAdUnit('postroll');
    if (prerollUnit.bids && 0 < prerollUnit.bids.length) {
      window.pbjs.addAdUnits(prerollUnit);
    }
    if (postrollUnit.bids && 0 < postrollUnit.bids.length) {
      window.pbjs.addAdUnits(postrollUnit);
    }

    window.pbjs.setConfig({
      cache: {
        url: 'https://prebid.adnxs.com/pbc/v1/cache',
      },
    });
  }
};

/**
 * Abbreviate keys to max length=20 as required by GPT
 *
 * The two problematic keys are hb_uuid_triplelift_postroll (27)
 * and hb_uuid_appnexus_postroll (25) which can be shortened to
 * hb_uuid_3lift_pst (17) and hb_uuid_appnexus_pst (20)
 *
 * @returns String
 */
const abbreviate = (bidTargeting, key, code) => {
  let bidder = bidTargeting.hb_bidder;

  // abbreviate triplelift
  if ('triplelift' === bidder) {
    bidder = '3lift';
  }
  // abbreviate preroll/postroll
  const position = 'preroll' === code ? 'pre' : 'pst';
  return `${key}_${bidder}_${position}`;
};

/**
 * Get the bid with the highest CPM from a list of bids
 *
 * The pbCg value refers to Custom Granularity and is a
 * rounded value after all bid adjustments have been applied
 *
 * @returns {object}
 */
const getBestBids = (bids = []) => [...bids].reduce((prev, current) => ((parseFloat(prev.pbCg) > parseFloat(current.pbCg)) ? prev : current), {}); // eslint-disable-line

/**
 * Promise that resolves with VAST 'cust_param' key-value pairs describing Prebid bids.
 *
 * @returns {Promise<Object>}
 */
export default () => new Promise((resolve, reject) => {
  console.log('HTLBID::bids.js::init');
  const prebidTimeout = window.prebidVideoTimeout || 2000;

  if (! window.isHtlbidEnabled) {
    reject();
    return;
  }

  window.htlbid.cmd.push(() => {
    window.pbjs.cmd.push(() => {
      // add video ad units, unless already added
      initializeUnits();

      // request bids from prebid partners
      const adUnitCodes = ['preroll', 'postroll'];

      window.pbjs.requestBids({
        timeout: prebidTimeout,
        adUnitCodes,
        bidsBackHandler: (bids) => {
          const targeting = {};

          // loop thru preroll + postroll results
          adUnitCodes.forEach((code) => {
            // get the highest CPM bid (if any)
            const best = getBestBids(bids?.[code]?.bids);

            if (best?.adserverTargeting) {
              // map default targeting key-values, like 'hb_pb=1.00'
              // to bidder + position-specific key-values, like
              // 'hb_pb_triplelift_post=1.00'
              const bidTargeting = best.adserverTargeting;
              bidTargeting.hb_bidder = best.bidderCode;
              Object.keys(bidTargeting).forEach((oldKey) => {
                const newKey = abbreviate(bidTargeting, oldKey, code);
                targeting[newKey] = bidTargeting[oldKey];
              });
            }
          });
          resolve(targeting);
        },
      });
    });
  });
});
